/**
 * The reports screen.
 *
 * WHAT IT IS FOR
 * --------------
 * Answering "how are we doing" — today, this week, this month, or on any one
 * day someone picks. The takings come first and largest, and everything under
 * them is there to explain the number: when it came in, where from, how it was
 * paid, what it cost, and what is still owed.
 *
 * WHY A RANGE, NOT JUST TODAY
 * ---------------------------
 * Today answers "is it a good day". A week or a month answers "is it a good
 * business", which is the question an owner actually acts on — a Tuesday dip
 * means nothing, a month of them means something. So the same figures are drawn
 * over whatever stretch of days is chosen, from one call to /reports/overview.
 *
 * FOOD COST IS STILL THE NUMBER THAT REVEALS PROBLEMS
 * ---------------------------------------------------
 * Sales going up is pleasant and not very informative. Food cost creeping from
 * 30% to 38% is theft, waste, or a supplier quietly raising prices — shown with
 * its caveat, because a percentage built partly on placeholder recipes looks
 * exactly as solid as one built on real numbers.
 */
import { useMemo, useState, Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatPKR } from '@foodrun/shared';

import { api, withQuery } from '../../lib/api.js';
import { ReportTabs } from './ReportTabs.jsx';
import { ReportReceipt } from './ReportReceipt.jsx';
import { TabBillsSheet } from './TabBillsSheet.jsx';
import { WebsiteOrdersSheet } from './WebsiteOrdersSheet.jsx';
import { ShiftPrintSheet } from './ShiftPrintSheet.jsx';
import {
  Donut,
  BarChart,
  TrendChart,
  HBars,
  CHANNEL_COLORS,
  CHANNEL_LABELS,
  paymentColor,
  paymentLabel,
} from './Charts.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { useTradingDay } from '../pos/useBusinessDay.js';

// --- Dates, done in the browser's own day so a picked date means what it says.
function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function parseYmd(s) {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}
function shiftDays(s, n) {
  const d = parseYmd(s);
  d.setDate(d.getDate() + n);
  return ymd(d);
}
const TODAY = ymd(new Date());

/** The [from, to] a mode and an anchor day resolve to. Never past the latest trading day. */
function rangeFor(mode, anchor, latest = TODAY, custom = null) {
  if (mode === 'day') return [anchor, anchor];
  // Any two days, chosen by hand; never past the latest trading day.
  if (mode === 'range' && custom) return [custom.from, custom.to > latest ? latest : custom.to];

  const d = parseYmd(anchor);
  if (mode === 'week') {
    // The Monday–Sunday week the day falls in.
    const dow = (d.getDay() + 6) % 7; // 0 = Monday
    const from = shiftDays(anchor, -dow);
    const to = shiftDays(from, 6);
    return [from, to > latest ? latest : to];
  }
  // month
  const from = ymd(new Date(d.getFullYear(), d.getMonth(), 1));
  const to = ymd(new Date(d.getFullYear(), d.getMonth() + 1, 0));
  return [from, to > latest ? latest : to];
}

function prettyDay(s) {
  return parseYmd(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * One entry per calendar day in the range, zeros filled in.
 *
 * The server returns only the days that traded, which is right for a total but
 * wrong for a trend: a week where one day sold would be a single point, and a
 * line needs two. Filled, the trend shows every day — the quiet ones as the
 * valleys they were.
 */
function fillDays(from, to, series) {
  const byDate = Object.fromEntries((series ?? []).map((r) => [r.date, r]));
  const out = [];
  let d = from;
  for (let guard = 0; d <= to && guard < 400; guard++) {
    const r = byDate[d];
    out.push({
      date: d,
      orders: Number(r?.orders) || 0,
      gross: Number(r?.gross) || 0,
      net: Number(r?.net) || 0,
    });
    d = shiftDays(d, 1);
  }
  return out;
}
function periodLabel(mode, from, to) {
  if (mode === 'day' || from === to) return prettyDay(from);
  if (mode === 'month') {
    return parseYmd(from).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }
  const a = parseYmd(from).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  const b = parseYmd(to).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return `${a} – ${b}`;
}

const CHANNEL_ORDER = ['dine_in', 'takeaway', 'delivery', 'foodpanda'];

export default function DashboardPage() {
  const [mode, setMode] = useState('day');
  // "Today" is the trading day the shop has open, which after midnight is
  // still last night; it may even be ahead of the calendar if the day was
  // closed early. Until the server has said, the calendar stands in.
  const tradingDay = useTradingDay();
  const latest = tradingDay && tradingDay > TODAY ? tradingDay : TODAY;
  const [picked, setAnchor] = useState(null);
  const anchor = picked ?? tradingDay ?? TODAY;
  // The two ends of a custom range, once somebody has set them.
  const [custom, setCustom] = useState(null);
  const [printing, setPrinting] = useState(false);
  // Whose bills the popup is showing, from the "Put on the tab" panel.
  const [tabBillsFor, setTabBillsFor] = useState(null);
  // The website tile clicked open: which orders to list ('all', 'accepted', …).
  const [websiteOrders, setWebsiteOrders] = useState(null);
  const [from, to] = useMemo(() => rangeFor(mode, anchor, latest, custom ?? { from: anchor, to: anchor }), [mode, anchor, latest, custom]);
  const includesToday = to >= (tradingDay ?? TODAY);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['reports', 'overview', from, to],
    queryFn: () => api.get(withQuery('/reports/overview', { from, to })),
    // Keep a live day moving on its own; a settled past period has no reason to.
    refetchInterval: includesToday ? 60_000 : false,
    // Hold the last period on screen while the next one loads, so switching
    // Day/Week/Month does not blink back to a spinner.
    placeholderData: (previous) => previous,
  });

  const controls = (
    <PeriodControls
      mode={mode}
      anchor={anchor}
      latest={latest}
      range={{ from, to }}
      onRange={(next) => setCustom(next)}
      onMode={setMode}
      onAnchor={setAnchor}
      label={periodLabel(mode, from, to)}
    />
  );

  if (isLoading && !data) {
    return (
      <div className="mx-auto max-w-6xl p-4">
        <Header controls={controls} />
        <div className="flex h-[50vh] items-center justify-center">
          <Spinner label="Loading the figures" />
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="mx-auto max-w-6xl p-4">
        <Header controls={controls} />
        <p className="py-24 text-center text-sm text-ink-2">
          Could not load the figures. Check the branch computer is running.
        </p>
      </div>
    );
  }

  const { summary, comparison, coverage } = data;
  const singleDay = data.period?.days === 1;
  const periodWord = mode === 'day' ? 'day' : mode === 'week' ? 'week' : mode === 'month' ? 'month' : 'period';
  // A period still running was compared with the one before it up to this same time of day.
  const sameTime = Boolean(comparison?.previous?.sameTime);
  const reportSubtitle = `${periodWord[0].toUpperCase()}${periodWord.slice(1)} report`;

  // The channel cards, in the order the shop thinks in, each with its share and
  // a little run of daily takings drawn beside it.
  const channelByType = Object.fromEntries((data.salesByChannel ?? []).map((r) => [r.order_type, r]));
  // Last period's takings for each channel, so a card can say up or down.
  const prevChannelByType = Object.fromEntries(
    (data.prevSalesByChannel ?? []).map((r) => [r.order_type, Number(r.revenue) || 0])
  );
  const channelChange = (type) => {
    const now = Number(channelByType[type]?.revenue) || 0;
    const before = prevChannelByType[type] ?? 0;
    return before > 0 ? Number((((now - before) / before) * 100).toFixed(1)) : null;
  };
  const daily = fillDays(data.period?.from ?? from, data.period?.to ?? to, data.dailySeries);
  const dates = daily.map((r) => r.date);
  const channelSeries = (type) => {
    const byDate = {};
    for (const r of data.dailyByChannel ?? []) {
      if (r.order_type === type) byDate[r.date] = Number(r.revenue) || 0;
    }
    return dates.map((d) => ({ date: d, revenue: byDate[d] ?? 0 }));
  };

  return (
    <div className="mx-auto max-w-6xl p-4">
      <Header controls={controls} onPrint={() => setPrinting(true)} />

      {/* The four an owner reads first. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Figure
          label="Takings"
          value={formatPKR(summary.grossSales)}
          change={comparison?.salesGrowth}
          periodWord={periodWord}
          sameTime={sameTime}
          emphasis
        />
        <Figure
          label="Orders"
          value={summary.totalOrders}
          change={comparison?.orderGrowth}
          periodWord={periodWord}
          sameTime={sameTime}
          // Sales only. The Orders tab lists every bill rung up, cancelled ones
          // included, so the two are reconciled here rather than left to differ.
          hint={Number(summary.voidOrders) > 0 ? `plus ${summary.voidOrders} cancelled, not counted` : null}
        />
        <Figure
          label="Profit"
          value={formatPKR(summary.netProfit ?? summary.grossProfit)}
          tone={(summary.netProfit ?? 0) < 0 ? 'bad' : null}
          hint="After food, overheads and refunds"
        />
        <Figure
          label="Food cost"
          value={`${summary.foodCostPercent}%`}
          tone={summary.foodCostPercent > 35 ? 'bad' : summary.foodCostPercent > 32 ? 'warn' : null}
          caveat={coverage?.complete === false}
        />
      </div>

      {coverage?.warning && (
        <p className="mt-3 rounded-lg bg-warn-bg px-3.5 py-2.5 text-sm text-warn">
          {coverage.warning} The food cost above is provisional until the kitchen confirms them.
        </p>
      )}

      {/* The shape of the takings over time: the hours of one day, or the run of
          days across a week or month. */}
      <Panel title={singleDay ? 'Through the day' : 'Revenue day by day'} className="mt-4">
        {singleDay ? (
          <BarChart
            data={data.salesByHour ?? []}
            valueOf={(r) => Number(r.revenue) || 0}
            labelOf={(r) => `${r.hour}h`}
            formatValue={formatPKR}
            labelEvery={2}
            caption="Takings by hour"
          />
        ) : (
          <TrendChart
            data={daily}
            xOf={(r) => prettyDay(r.date)}
            yOf={(r) => Number(r.gross) || 0}
            formatValue={formatPKR}
            caption="Takings by day"
          />
        )}
      </Panel>

      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        <Panel title="Where it came from">
          <Donut
            data={(data.salesByChannel ?? []).map((r) => ({
              label: CHANNEL_LABELS[r.order_type] ?? r.order_type,
              value: Number(r.revenue) || 0,
              color: CHANNEL_COLORS[r.order_type] ?? '#6bc940',
            }))}
            formatValue={formatPKR}
            caption="Sales by channel"
            centerLabel="in sales"
          />
        </Panel>

        <Panel title="How it was paid">
          <Donut
            data={(data.paymentMix ?? []).map((r, i) => ({
              label: paymentLabel(r.method),
              value: Number(r.amount) || 0,
              color: paymentColor(r.method, i),
            }))}
            formatValue={formatPKR}
            caption="Payments by method"
            centerLabel="taken"
          />
          {data.cashCheck && <CashCheck check={data.cashCheck} />}
        </Panel>
      </div>

      {/* What these days should have left in the drawers, by the shop's own
          rule — the shift slip's "cash in hand", for the period chosen. */}
      {data.actualCash && <ActualCash cash={data.actualCash} />}

      {/* Each channel on its own, because a delivery problem and a dine-in
          problem are different problems. */}
      <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {CHANNEL_ORDER.map((type) => (
          <ChannelCard
            key={type}
            type={type}
            row={channelByType[type]}
            total={summary.grossSales}
            change={channelChange(type)}
            periodWord={periodWord}
            sameTime={sameTime}
            series={singleDay ? null : channelSeries(type)}
          />
        ))}
      </div>

      {/* Orders that came through the website, beside the till's — the owner
          asked how many, and that is the figure, with what they took. */}
      {data.website && (
        <div className="mt-3 flex flex-wrap items-baseline justify-between gap-3 rounded-xl border border-rule bg-surface p-3.5">
          <div>
            <p className="text-sm font-medium text-ink-2">From the website</p>
            <p className="text-xs text-ink-3">
              {data.website.orders === 0
                ? 'No website orders in these days.'
                : `${data.website.orders} of ${summary.totalOrders} orders (${data.website.share}%)` +
                  Object.entries(data.website.byType ?? {})
                    .map(([type, v]) => ` · ${v.orders} ${(CHANNEL_LABELS[type] ?? type).toLowerCase()}`)
                    .join('')}
            </p>
          </div>
          <span className="text-xl font-semibold tabular-nums text-ink">{formatPKR(data.website.revenue)}</span>
          {data.website.requests?.total > 0 && <WebsiteRequests requests={data.website.requests} onOpen={setWebsiteOrders} />}
        </div>
      )}

      {/* The one watched staff tab, on its own beneath the channels — what the
          Food Vlogger owes right now, shown even when it is settled to zero. A
          receivable, so it is a running total, not this period's figure. */}
      {data.trackedAccount && (
        <div className="mt-3 rounded-xl border border-rule bg-surface p-3.5">
          <div className="flex items-baseline justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-ink-2">{data.trackedAccount.name}</p>
              <p className="text-xs text-ink-3">On the tab, owed right now</p>
            </div>
            <span
              className={`text-xl font-semibold tabular-nums ${
                Number(data.trackedAccount.balance) > 0 ? 'text-warn' : 'text-ink'
              }`}
            >
              {formatPKR(Number(data.trackedAccount.balance) || 0)}
            </span>
          </div>
        </div>
      )}

      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        {!singleDay && (
          <Panel title="Busiest hours">
            <BarChart
              data={data.salesByHour ?? []}
              valueOf={(r) => Number(r.revenue) || 0}
              labelOf={(r) => `${r.hour}h`}
              formatValue={formatPKR}
              labelEvery={2}
              caption="Takings by hour across the period"
            />
          </Panel>
        )}

        <Panel title="Selling best" className={singleDay ? 'lg:col-span-2' : ''}>
          <HBars
            data={(data.topProducts ?? []).slice(0, 8)}
            labelOf={(r) => r.name}
            valueOf={(r) => Number(r.revenue) || 0}
            subOf={(r) => `${r.qty_sold ?? r.units_sold}×`}
            formatValue={formatPKR}
          />
        </Panel>
      </div>

      {/* What staff put on their accounts in the dates chosen — who ate, and
          how much. Unlike the panel below it, this one follows the period:
          somebody who ate on the 10th is here on the 10th and gone on the 11th. */}
      <Panel
        title={
          singleDay
            ? `Put on the tab on ${prettyDay(data.period.from)}`
            : `Put on the tab, ${prettyDay(data.period.from)} to ${prettyDay(data.period.to)}`
        }
        className="mt-3"
      >
        {(data.staffCharged ?? []).length === 0 ? (
          <p className="py-6 text-center text-sm text-ink-3">
            {singleDay
              ? 'Nothing went on a staff account that day.'
              : 'Nothing went on a staff account in these dates.'}
          </p>
        ) : (
          <>
            <div className="mb-3 flex items-baseline justify-between border-b border-rule pb-2">
              <span className="text-sm text-ink-2">Put on account, in total</span>
              <span className="text-lg font-semibold tabular-nums text-ink">
                {formatPKR(data.staffChargedTotal)}
              </span>
            </div>
            <ul className="space-y-1.5">
              {data.staffCharged.map((s) => (
                <li key={s.id} className="flex items-baseline justify-between gap-2">
                  <span className="min-w-0 truncate text-sm text-ink">
                    {s.name}
                    <span className="text-ink-3"> · </span>
                    {/* The bills themselves are one press away. */}
                    <button
                      type="button"
                      onClick={() => setTabBillsFor(s)}
                      className="rounded px-1 py-0.5 font-medium text-brand-300 underline-offset-2 hover:underline"
                    >
                      {s.bills} {s.bills === 1 ? 'bill' : 'bills'}
                    </button>
                  </span>
                  <span className="shrink-0 text-sm font-medium tabular-nums text-ink-2">
                    {formatPKR(s.amount)}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </Panel>

      {/* Money the shop is owed but has not taken — the staff tabs. A receivable,
          so it is a running total, not a figure for this period. */}
      {/* The shifts these days were sold on, each with its slip's figures, and
          whatever was sold on none of them. The total line is the day, so the
          slips can be matched to the report at a glance. */}
      {data.byShift && (
        <Panel title="By shift" className="mt-3">
          <ByShift byShift={data.byShift} daySales={summary.grossSales} />
        </Panel>
      )}

      <Panel title="On the tab" className="mt-3">
        {(data.staffOwed ?? []).length === 0 ? (
          <p className="py-6 text-center text-sm text-ink-3">Nobody owes anything.</p>
        ) : (
          <>
            <div className="mb-3 flex items-baseline justify-between border-b border-rule pb-2">
              <span className="text-sm text-ink-2">Owed to the shop, in total</span>
              <span className="text-lg font-semibold tabular-nums text-warn">
                {formatPKR(data.staffOwedTotal)}
              </span>
            </div>
            <ul className="space-y-1.5">
              {data.staffOwed.map((s) => (
                <li key={s.id} className="flex items-baseline justify-between gap-2">
                  <span className="min-w-0 truncate text-sm text-ink">{s.name}</span>
                  <span className="shrink-0 text-sm font-medium tabular-nums text-ink-2">
                    {formatPKR(Number(s.balance))}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </Panel>

      {/* The rest of the ledger for the period: what came off the top, what was
          spent, what went out or was lost. */}
      <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Small label="Average order" value={formatPKR(summary.avgOrderValue)} />
        <Small label="Delivery charges" value={formatPKR(summary.deliveryFees ?? 0)} />
        <Small label="Tax collected" value={formatPKR(summary.tax)} />
        <Small label="Discounts given" value={formatPKR(summary.discounts)} />
        <Small label="Overheads" value={formatPKR(summary.operatingExpenses ?? 0)} />
        <Small label="Food cost" value={formatPKR(summary.cogs)} />
        <Small label="Refunded" value={formatPKR(summary.refunds)} tone={summary.refunds > 0} />
        <Small label="Wasted" value={formatPKR(summary.wastage)} tone={summary.wastage > 0} />
      </div>

      {(data.expenses?.byCategory ?? []).length > 0 && (
        <Panel title="What was spent" className="mt-3">
          <HBars
            data={data.expenses.byCategory.slice(0, 8)}
            labelOf={(r) => r.category}
            valueOf={(r) => Number(r.amount) || 0}
            formatValue={formatPKR}
            color="#f5b544"
          />
        </Panel>
      )}

      {/* The day's figures on the thermal roll — the shift-close report's shape,
          for a chosen day rather than a shift. */}
      {printing && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Print report"
          className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-black/70 p-4"
          onClick={() => setPrinting(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-surface p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="max-h-[70vh] overflow-y-auto rounded-lg border border-rule">
              <ReportReceipt
                title="Sales report"
                subtitle={reportSubtitle}
                meta={[periodLabel(mode, from, to)]}
                sections={summarySections(data)}
              />
            </div>
            <div className="no-print mt-3 flex gap-2">
              <Button
                variant="secondary"
                size="lg"
                className="flex-1"
                onClick={() => setPrinting(false)}
              >
                Close
              </Button>
              <Button size="lg" className="flex-1" onClick={() => window.print()}>
                Print
              </Button>
            </div>
          </div>
        </div>
      )}

      {websiteOrders && (
        <WebsiteOrdersSheet status={websiteOrders} from={data.period.from} to={data.period.to} onClose={() => setWebsiteOrders(null)} />
      )}

      {tabBillsFor && (
        <TabBillsSheet
          person={tabBillsFor}
          from={data.period.from}
          to={data.period.to}
          onClose={() => setTabBillsFor(null)}
        />
      )}
    </div>
  );
}

/** The shift-close-style summary, as sections of label and figure. */
function summarySections(data) {
  const s = data.summary;
  const m = formatPKR;
  const offTheTop = [
    Number(s.discounts) > 0 && { label: 'Discounts', value: m(s.discounts) },
    Number(s.deliveryFees) > 0 && { label: 'Delivery charges', value: m(s.deliveryFees) },
    Number(s.tax) > 0 && { label: 'Tax', value: m(s.tax) },
    Number(s.refunds) > 0 && { label: 'Refunds', value: m(s.refunds) },
    Number(s.wastage) > 0 && { label: 'Wasted', value: m(s.wastage) },
    Number(s.voidOrders) > 0 && { label: 'Voided orders', value: s.voidOrders },
  ].filter(Boolean);

  return [
    {
      rows: [
        { label: 'Takings', value: m(s.grossSales), strong: true },
        data.cashCheck?.returned > 0 && { label: 'Sales returned', value: `-${m(data.cashCheck.returned)}` },
        data.cashCheck?.returned > 0 && { label: 'Takings after returns', value: m(data.cashCheck.netTakings) },
        { label: 'Orders', value: s.totalOrders },
        { label: 'Net sales', value: m(s.netSales) },
      ].filter(Boolean),
    },
    {
      heading: 'Where it came from',
      rows: [
        ...(data.salesByChannel ?? []).map((r) => ({
          label: CHANNEL_LABELS[r.order_type] ?? r.order_type,
          value: m(Number(r.revenue) || 0),
        })),
        data.website && { label: `  via the website (${data.website.orders} orders)`, value: m(data.website.revenue) },
      ].filter(Boolean),
    },
    ...paidSections(data, m),
    ...actualCashSection(data, m),
    { heading: 'Off the top', rows: offTheTop },
    {
      heading: 'Best sellers',
      rows: (data.topProducts ?? []).slice(0, 5).map((r, i) => ({
        label: `${i + 1}. ${r.name}`,
        value: m(Number(r.revenue) || 0),
      })),
    },
    // The one watched tab (the vlogger's) and no other, shown even when it is
    // settled to zero — the account the owner tracks on the printed report.
    data.trackedAccount && {
      heading: 'On the tab',
      rows: [
        { label: data.trackedAccount.name, value: m(Number(data.trackedAccount.balance) || 0) },
      ],
    },
  ].filter(Boolean);
}

/**
 * "How it was paid", with the cash check: from the takings down to the cash
 * received, each step a line, so the slip explains its own figures. Without
 * `cashCheck` (an older server) it prints the plain payment mix as before.
 */
function paidSections(data, m) {
  const c = data.cashCheck;
  if (!c) {
    return [
      {
        heading: 'How it was paid',
        rows: (data.paymentMix ?? []).map((r) => ({ label: paymentLabel(r.method), value: m(Number(r.amount) || 0) })),
      },
    ];
  }
  const paid = [
    { label: 'Cash received', value: m(c.cashReceived) },
    c.wrongPayment > 0 && { label: `  wrong payments, paid again (${c.wrongPaymentCount})`, value: m(c.wrongPayment) },
    c.refundsOutCash > 0 && { label: '  refunds handed back in cash', value: `-${m(c.refundsOutCash)}` },
    c.refundsOutCash > 0 && { label: 'Cash kept', value: m(c.cashKept), strong: true },
    { label: 'Online', value: m(c.online) },
    { label: 'Card', value: m(c.card) },
    c.onAccount > 0 && { label: 'On the tab', value: m(c.onAccount) },
    c.foodpanda > 0 && { label: 'Foodpanda (owed)', value: m(c.foodpanda) },
  ].filter(Boolean);
  const timing = [
    c.paidAfter > 0 && { label: 'Bills of these days, paid on a later day', value: `-${m(c.paidAfter)}` },
    c.unpaid > 0 && { label: 'Bills of these days, not yet paid', value: `-${m(c.unpaid)}` },
    c.paidEarlier > 0 && { label: 'Bills of earlier days, paid on these days', value: `+${m(c.paidEarlier)}` },
  ].filter(Boolean);
  const check = [
    { label: 'Takings', value: m(c.takings) },
    { label: '- sold but not paid in cash (Foodpanda, online, card, on the tab)', value: `-${m(c.foodpanda + c.online + c.card + c.onAccount)}` },
    (c.paidAfter > 0 || c.unpaid > 0) && { label: '- bills of these days paid later or not yet', value: `-${m(c.paidAfter + c.unpaid)}` },
    c.paidEarlier > 0 && { label: '+ earlier bills paid on these days', value: `+${m(c.paidEarlier)}` },
    c.wrongPayment > 0 && { label: '+ wrong payments refunded, paid again', value: `+${m(c.wrongPayment)}` },
    { label: '= Cash received', value: `${m(c.expectedCash)} ${Math.abs(c.difference) < 0.5 ? '✓' : `(off by ${m(c.difference)})`}`, strong: true },
  ].filter(Boolean);
  return [
    { heading: 'How it was paid', rows: paid },
    timing.length ? { heading: 'Timing', rows: timing } : null,
    { heading: 'Cash check', rows: check },
  ].filter(Boolean);
}

/**
 * The same check on screen, under the payment donut — and, because the line
 * names alone mean little to an owner, each line has a "what is this?" that
 * opens a plain-words note naming the bills behind the figure.
 */
function CashCheck({ check: c }) {
  const m = formatPKR;
  const [open, setOpen] = useState(null);
  const ties = Math.abs(c.difference) < 0.5;
  const bills = c.bills ?? {};
  const list = (rows, line) =>
    rows?.length ? (
      <ul className="mt-1.5 space-y-0.5">
        {rows.map((b, i) => (
          <li key={`${b.orderNumber}-${i}`} className="flex justify-between gap-3 tabular-nums">
            <span>{line(b)}</span>
            <span>{m(b.amount)}</span>
          </li>
        ))}
      </ul>
    ) : null;
  const notCash = c.foodpanda + c.online + c.card + c.onAccount;
  const rows = [
    {
      key: 'takings',
      label: 'Takings',
      value: m(c.takings),
      note: 'Every bill of these days added up, after discounts and including delivery charges. This is the sales figure at the top of the page.',
    },
    {
      key: 'notCash',
      label: 'Sold, but not paid in cash',
      value: `− ${m(notCash)}`,
      note: (
        <>
          Sales where no cash came over the counter: Foodpanda {m(c.foodpanda)} (Foodpanda owes it), online {m(c.online)}, card {m(c.card)}, put on staff accounts {m(c.onAccount)} (owed by the staff member).
        </>
      ),
    },
    (c.paidAfter > 0 || c.unpaid > 0) && {
      key: 'later',
      label: 'Bills of these days not yet paid, or paid on a later day',
      value: `− ${m(c.paidAfter + c.unpaid)}`,
      note: (
        <>
          A bill counts in sales on the day it was rung up, but in cash on the day the money arrived. These bills are in the takings above, but their money is not in these days’ cash.
          {list(bills.paidAfter, (b) => `${b.orderNumber} — paid ${b.paidOn}${b.method ? ` by ${b.method}` : ''}`)}
          {list(bills.unpaid, (b) => `${b.orderNumber} — still unpaid`)}
        </>
      ),
    },
    c.paidEarlier > 0 && {
      key: 'earlier',
      label: 'Bills of earlier days paid on these days',
      value: `+ ${m(c.paidEarlier)}`,
      note: (
        <>
          Money that arrived on these days for bills rung up before them. It is in these days’ cash but not in the takings above.
          {list(bills.paidEarlier, (b) => `${b.orderNumber} — bill of ${b.billDay}`)}
        </>
      ),
    },
    c.wrongPayment > 0 && {
      key: 'wrong',
      label: 'Wrong payments refunded, then paid again',
      value: `+ ${m(c.wrongPayment)}`,
      note: (
        <>
          Each of these bills was paid, the payment was found to be wrong and handed back, and the bill was paid again. One sale, counted once in the takings — but the cash came in twice, so it is counted twice in cash received. The refund that went back out is on the “refunds handed back” line.
          {list(bills.wrongPayment, (b) => `${b.orderNumber} — ${b.day}${b.reason ? `, ${b.reason}` : ''}`)}
        </>
      ),
    },
    {
      key: 'received',
      label: 'Cash received',
      value: m(c.cashReceived),
      strong: true,
      note: 'All the cash that came over the counter on these days, before anything was handed back. The lines above add up to exactly this when every rupee is accounted for.',
    },
    c.refundsOutCash > 0 && {
      key: 'out',
      label: 'Refunds handed back in cash',
      value: `− ${m(c.refundsOutCash)}`,
      note: (
        <>
          Cash the cashier gave back to customers on these days, whatever the reason — a wrong payment or a returned sale. It left the drawer, so it comes off the cash received.
          {list(bills.refundsOut, (b) => `${b.orderNumber} — ${b.day}`)}
        </>
      ),
    },
    c.refundsOutCash > 0 && {
      key: 'kept',
      label: 'Cash kept',
      value: m(c.cashKept),
      strong: true,
      note: 'What the drawers should have held for these days once refunds are out: cash received less refunds handed back.',
    },
  ].filter(Boolean);
  return (
    <div className="mt-3 border-t border-rule pt-3">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-ink-3">Cash check</span>
        <span className={`text-xs font-medium ${ties ? 'text-good' : 'text-warn'}`}>{ties ? 'Ties to the rupee' : `Off by ${m(c.difference)}`}</span>
      </div>
      <p className="mb-2 text-xs text-ink-3">From the takings down to the cash, one line per reason they differ. Press ⓘ on a line for what it means and which bills are in it.</p>
      <dl className="space-y-1 text-sm">
        {rows.map((r) => (
          <div key={r.key}>
            <div className="flex items-baseline justify-between gap-3">
              <dt className={`flex items-center gap-1.5 ${r.strong ? 'font-medium text-ink' : 'text-ink-2'}`}>
                <span>{r.label}</span>
                <button
                  type="button"
                  onClick={() => setOpen(open === r.key ? null : r.key)}
                  aria-expanded={open === r.key}
                  aria-label={`What is “${r.label}”?`}
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-rule text-[11px] text-ink-3 hover:border-brand-400 hover:text-ink"
                >
                  i
                </button>
              </dt>
              <dd className={`tabular-nums ${r.strong ? 'font-medium text-ink' : 'text-ink-2'}`}>{r.value}</dd>
            </div>
            {open === r.key && (
              <div className="mb-1 mt-1 rounded-lg bg-surface-3 px-3 py-2 text-xs leading-relaxed text-ink-2">{r.note}</div>
            )}
          </div>
        ))}
      </dl>
      {c.returned > 0 && (
        <p className="mt-2 text-xs text-ink-3">
          Sales returned (refunded, not sold again): {m(c.returned)} — takings after returns {m(c.netTakings)}.
        </p>
      )}
    </div>
  );
}

/**
 * "Actual cash" on the printed slip: the shop's own drawer rule, line by line,
 * so the figure can be set beside the cashier's sheet without working anything
 * out by hand. Nothing without `actualCash` (an older server).
 */
function actualCashSection(data, m) {
  const a = data.actualCash;
  if (!a) return [];
  return [
    {
      heading: 'Actual cash',
      rows: [
        { label: 'Cash received', value: m(a.cashReceived) },
        a.tabPayments > 0 && { label: '+ staff paying off their tabs', value: `+${m(a.tabPayments)}` },
        a.refundsOutCash > 0 && { label: '- refunds handed back in cash', value: `-${m(a.refundsOutCash)}` },
        a.deliveryCharges > 0 && { label: '- delivery charges (riders)', value: `-${m(a.deliveryCharges)}` },
        a.discounts > 0 && { label: '- discounts given', value: `-${m(a.discounts)}` },
        { label: '= Actual cash', value: m(a.actual), strong: true },
        a.onAccount > 0 && { label: 'Put on staff tabs (no cash came in)', value: m(a.onAccount) },
      ].filter(Boolean),
    },
  ];
}

/**
 * The actual cash, on screen: what these days should have left in the drawers
 * by this shop's rule — the same arithmetic as the shift slip's "cash in
 * hand", for whatever period is chosen. Cash received and staff paying off
 * their tabs come in; refunds handed back, the riders' delivery charges and
 * the discounts given come out. What was put on staff tabs is named at the
 * foot so nobody takes it off a second time: no cash ever came in for it.
 */
function ActualCash({ cash: a }) {
  const m = formatPKR;
  const [open, setOpen] = useState(null);
  const rows = [
    {
      key: 'received',
      label: 'Cash received',
      value: m(a.cashReceived),
      note: 'All the cash taken over the counter on these days, before anything went back out — the same figure as in the cash check.',
    },
    a.tabPayments > 0 && {
      key: 'tab',
      label: `Staff paying off their tabs (${a.tabPaymentsCount})`,
      value: `+ ${m(a.tabPayments)}`,
      note: 'Cash handed to the cashier by staff clearing what they owed on their account. It is not a sale, so it is in no sales figure — but it is in the drawer.',
    },
    a.refundsOutCash > 0 && {
      key: 'refunds',
      label: 'Refunds handed back in cash',
      value: `− ${m(a.refundsOutCash)}`,
      note: 'Cash given back to customers on these days, for a returned sale or a wrong payment. It left the drawer.',
    },
    a.deliveryCharges > 0 && {
      key: 'delivery',
      label: 'Delivery charges',
      value: `− ${m(a.deliveryCharges)}`,
      note: 'The delivery charge on every delivery bill of these days. The rider is paid from the drawer whichever way the customer paid, so the whole figure comes out of the cash.',
    },
    a.discounts > 0 && {
      key: 'discounts',
      label: 'Discounts given',
      value: `− ${m(a.discounts)}`,
      note: 'Every discount given on these days, taken off the drawer the way the shift slip and the cashier’s sheet take it off. Note that the bills in this system are already after discount — so this line follows the shop’s own rule, not the bills.',
    },
  ].filter(Boolean);
  return (
    <Panel title="Actual cash" className="mt-3">
      <p className="mb-3 text-xs text-ink-3">
        What these days should have left in the drawers, by the same rule as the shift slip: cash in, less what went out of it. Press ⓘ on a line for what it means.
      </p>
      <dl className="space-y-1 text-sm">
        {rows.map((r) => (
          <div key={r.key}>
            <div className="flex items-baseline justify-between gap-3">
              <dt className="flex items-center gap-1.5 text-ink-2">
                <span>{r.label}</span>
                <button
                  type="button"
                  onClick={() => setOpen(open === r.key ? null : r.key)}
                  aria-expanded={open === r.key}
                  aria-label={`What is “${r.label}”?`}
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-rule text-[11px] text-ink-3 hover:border-brand-400 hover:text-ink"
                >
                  i
                </button>
              </dt>
              <dd className="tabular-nums text-ink-2">{r.value}</dd>
            </div>
            {open === r.key && (
              <div className="mb-1 mt-1 rounded-lg bg-surface-3 px-3 py-2 text-xs leading-relaxed text-ink-2">{r.note}</div>
            )}
          </div>
        ))}
      </dl>
      <div className="mt-3 flex items-baseline justify-between gap-3 border-t border-rule pt-3">
        <span className="text-sm font-medium text-ink">Actual cash</span>
        <span className="text-xl font-semibold tabular-nums text-ink">{m(a.actual)}</span>
      </div>
      {a.onAccount > 0 && (
        <p className="mt-2 text-xs text-ink-3">
          Put on staff tabs: {m(a.onAccount)}. No cash came in for those bills, so they are in none of the lines above — nothing to take off.
        </p>
      )}
    </Panel>
  );
}

function Header({ controls, onPrint }) {
  return (
    <div className="mb-4">
      <ReportTabs />
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">{controls}</div>
        {onPrint && (
          <button
            type="button"
            onClick={onPrint}
            className="no-print shrink-0 rounded-lg border border-rule px-3 py-1.5 text-sm font-medium text-ink-2 hover:border-brand-400 hover:text-ink"
          >
            Print report
          </button>
        )}
      </div>
    </div>
  );
}

function PeriodControls({ mode, anchor, onMode, onAnchor, label, latest = TODAY, range, onRange }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="inline-flex rounded-lg border border-rule bg-surface-3 p-0.5">
        {[
          ['day', 'Daily'],
          ['week', 'Weekly'],
          ['month', 'Monthly'],
          ['range', 'Range'],
        ].map(([m, text]) => (
          <button
            key={m}
            type="button"
            onClick={() => onMode(m)}
            aria-pressed={mode === m}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              mode === m ? 'bg-brand-500 text-black' : 'text-ink-2 hover:text-ink'
            }`}
          >
            {text}
          </button>
        ))}
      </div>

      {mode === 'range' ? (
        <RangeInputs range={range} latest={latest} onRange={onRange} />
      ) : (
        <label className="flex items-center gap-2 text-sm text-ink-2">
          <span className="text-ink-3">On</span>
          <input
            type="date"
            value={anchor}
            max={latest}
            onChange={(e) => e.target.value && onAnchor(e.target.value)}
            aria-label="Pick a day"
            className="h-9 rounded-lg border border-rule bg-surface-3 px-2.5 text-sm text-ink focus:border-brand-400 focus:outline-none"
          />
        </label>
      )}

      <span className="ml-auto text-sm text-ink-3">{label}</span>
    </div>
  );
}

/** Two trading days, from and to, kept in order and never past the latest. */
export function RangeInputs({ range, latest, onRange }) {
  const box = 'h-9 rounded-lg border border-rule bg-surface-3 px-2.5 text-sm text-ink focus:border-brand-400 focus:outline-none';
  return (
    <>
      <label className="flex items-center gap-2 text-sm text-ink-2">
        <span className="text-ink-3">From</span>
        <input
          type="date"
          value={range.from}
          max={range.to}
          onChange={(e) => e.target.value && onRange({ from: e.target.value, to: range.to < e.target.value ? e.target.value : range.to })}
          aria-label="From day"
          className={box}
        />
      </label>
      <label className="flex items-center gap-2 text-sm text-ink-2">
        <span className="text-ink-3">To</span>
        <input
          type="date"
          value={range.to}
          min={range.from}
          max={latest}
          onChange={(e) => e.target.value && onRange({ from: range.from > e.target.value ? e.target.value : range.from, to: e.target.value })}
          aria-label="To day"
          className={box}
        />
      </label>
    </>
  );
}

function ChannelCard({ type, row, total, change, periodWord, sameTime = false, series }) {
  const color = CHANNEL_COLORS[type] ?? '#6bc940';
  const revenue = Number(row?.revenue) || 0;
  const orders = Number(row?.orders) || 0;
  const share = total > 0 ? Math.round((revenue / total) * 100) : 0;
  const avg = orders > 0 ? revenue / orders : 0;

  return (
    <div className="rounded-xl border border-rule bg-surface p-3.5">
      <div className="flex items-center gap-2">
        <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: color }} />
        <span className="text-sm font-medium text-ink-2">{CHANNEL_LABELS[type] ?? type}</span>
        <span className="ml-auto text-xs tabular-nums text-ink-3">{share}%</span>
      </div>
      <p className="mt-1.5 font-semibold text-lg tabular-nums text-ink">{formatPKR(revenue)}</p>
      <p className="text-xs text-ink-3">
        {orders} order{orders === 1 ? '' : 's'} · {formatPKR(avg)} avg
      </p>
      {/* Whether this channel on its own was up or down — the same read the
          Takings and Orders figures give, per channel. */}
      {Number.isFinite(change) && change !== 0 && (
        <p className={`mt-0.5 text-xs ${change > 0 ? 'text-good' : 'text-bad'}`}>
          {change > 0 ? '+' : ''}
          {change}% vs previous {periodWord}
          {sameTime ? ' to this time' : ''}
        </p>
      )}
      {series && series.length > 1 && (
        <div className="mt-2">
          <BarChart
            data={series}
            valueOf={(r) => r.revenue}
            labelOf={(r) => r.date}
            formatValue={formatPKR}
            color={color}
            maxPx={28}
            showLabels={false}
            caption={`${CHANNEL_LABELS[type] ?? type} by day`}
          />
        </div>
      )}
    </div>
  );
}

function Figure({ label, value, change, periodWord, sameTime = false, tone, emphasis, caveat, hint }) {
  const colour = tone === 'bad' ? 'text-bad' : tone === 'warn' ? 'text-warn' : 'text-ink';

  return (
    <div className="rounded-xl border border-rule bg-surface p-3.5">
      <p className="text-xs text-ink-3">
        {label}
        {caveat && (
          <span className="ml-1" title="Partly based on placeholder recipes">
            *
          </span>
        )}
      </p>
      <p className={`mt-1 font-semibold tabular-nums ${emphasis ? 'text-2xl' : 'text-xl'} ${colour}`}>
        {value}
      </p>
      {/* Only shown when there is something to compare against — a first period
          with no "before" has nothing to be up or down against. */}
      {Number.isFinite(change) && change !== 0 && (
        <p className={`mt-0.5 text-xs ${change > 0 ? 'text-good' : 'text-bad'}`}>
          {change > 0 ? '+' : ''}
          {change}% vs previous {periodWord}
          {sameTime ? ' to this time' : ''}
        </p>
      )}
      {/* A note that is true whatever the comparison says, so it is never hidden by one. */}
      {hint && <p className="mt-0.5 text-xs text-ink-3">{hint}</p>}
    </div>
  );
}

function Small({ label, value, tone }) {
  return (
    <div className="rounded-lg border border-rule bg-surface-3 px-3 py-2.5">
      <p className="text-xs text-ink-3">{label}</p>
      <p className={`mt-0.5 font-medium tabular-nums ${tone ? 'text-warn' : 'text-ink'}`}>{value}</p>
    </div>
  );
}

/**
 * The two shifts of each trading day — the day shift, opened around noon,
 * and the night shift, opened after midnight — one row each, adding up to
 * the day. The slot comes from when the shift was opened, not from what the
 * cashier typed. A bill of this day that was filed on some other day's shift
 * is folded into the slot its own time falls in and named under the row.
 */
function ByShift({ byShift, daySales }) {
  // Which shift's slip is being printed again, if any.
  const [printShift, setPrintShift] = useState(null);
  const cols = ['sales', 'cash', 'card', 'online', 'account', 'foodpanda', 'refunds'];
  const head = { sales: 'Sales', cash: 'Cash', card: 'Card', online: 'Online', account: 'On account', foodpanda: 'Foodpanda', refunds: 'Refunds' };
  const days = byShift.days ?? [];
  const matches = Math.abs(byShift.total.sales - Number(daySales || 0)) < 0.01;
  const cell = 'whitespace-nowrap px-2 py-1.5 text-right tabular-nums';
  const short = (num) => num.replace(/^SH-[A-Z0-9]+-0*/, 'SH-');

  if (!days.length) return <p className="text-sm text-ink-3">No shifts on these days.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-ink-3">
            <th className="px-2 py-1.5 text-left font-medium">Shift</th>
            <th className="px-2 py-1.5 text-left font-medium">Cashier</th>
            <th className="px-2 py-1.5" aria-label="Print" />
            <th className={`${cell} font-medium`}>Orders</th>
            {cols.map((c) => (
              <th key={c} className={`${cell} font-medium`}>{head[c]}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {days.map((d) => (
            <Fragment key={d.day}>
              {days.length > 1 && (
                <tr className="border-t border-rule bg-surface-3/50">
                  <td className="px-2 py-1 text-xs font-medium text-ink-2" colSpan={4 + cols.length}>{prettyDay(d.day)}</td>
                </tr>
              )}
              {d.slots.map((r) => {
                const empty = !r.shifts.length && !r.strays.length;
                return (
                  <tr key={`${d.day}-${r.slot}`} className={`border-t border-rule ${empty ? 'text-ink-3' : ''}`}>
                    <td className="px-2 py-1.5 text-ink">
                      <span className={empty ? 'text-ink-3' : 'font-medium'}>{r.label}</span>
                      {r.shifts.map((sh) => (
                        <span key={sh.id} className="text-ink-3">
                          {' '}· {short(sh.shiftNumber)}
                          {sh.status === 'open' && <span className="ml-1 rounded-full bg-brand-400/15 px-1.5 text-xs text-brand-300">open</span>}
                        </span>
                      ))}
                      {empty && <span className="text-xs text-ink-3"> · no shift opened</span>}
                      {r.strays.length > 0 && (
                        <span className="block text-xs text-ink-3">
                          {r.strays.map((st) => `+${st.orders} ${st.orders === 1 ? 'bill' : 'bills'} filed on ${short(st.shiftNumber)}`).join(', ')}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-ink-2">{[...new Set(r.shifts.map((sh) => sh.cashierName))].join(', ')}</td>
                    {/* The slip for each shift in the row, in the format the
                        cashier was handed at close, with the figures as they stand now. */}
                    <td className="px-2 py-1.5">
                      <div className="flex flex-wrap gap-1">
                        {r.shifts.map((sh) => (
                          <button
                            key={sh.id}
                            type="button"
                            onClick={() => setPrintShift(sh.id)}
                            aria-label={`Print report for ${sh.shiftNumber}`}
                            className="no-print whitespace-nowrap rounded-lg border border-rule px-2 py-0.5 text-xs font-medium text-ink-2 hover:border-brand-400 hover:text-ink"
                          >
                            Print report
                          </button>
                        ))}
                      </div>
                    </td>
                    <td className={`${cell} text-ink-2`}>{r.orders || '—'}</td>
                    {cols.map((c) => (
                      <td key={c} className={`${cell} ${c === 'sales' ? 'font-medium text-ink' : 'text-ink-2'}`}>{Number(r[c]) ? formatPKR(r[c]) : '—'}</td>
                    ))}
                  </tr>
                );
              })}
              {d.unfiled.orders > 0 && (
                <tr className="border-t border-rule text-warn">
                  <td className="px-2 py-1.5" colSpan={3}>Rung up with no shift open</td>
                  <td className={cell}>{d.unfiled.orders}</td>
                  <td className={`${cell} font-medium`}>{formatPKR(d.unfiled.sales)}</td>
                  <td colSpan={cols.length - 1} />
                </tr>
              )}
            </Fragment>
          ))}
          <tr className="border-t-2 border-rule font-semibold text-ink">
            <td className="px-2 py-1.5" colSpan={3}>Total</td>
            <td className={cell}>{byShift.total.orders}</td>
            {cols.map((c) => (
              <td key={c} className={cell}>{formatPKR(byShift.total[c] ?? 0)}</td>
            ))}
          </tr>
        </tbody>
      </table>
      <p className={`mt-2 text-xs ${matches ? 'text-ink-3' : 'text-warn'}`}>
        {matches ? 'The two shifts add up to the day’s takings.' : `The shifts add up to ${formatPKR(byShift.total.sales)}; the day’s takings are ${formatPKR(daySales)}.`}
        {' '}Day shift is the one opened in daytime (6 am to 6 pm), night shift the one opened after; the name typed at opening is not used.
      </p>
      {printShift && <ShiftPrintSheet shiftId={printShift} onClose={() => setPrintShift(null)} />}
    </div>
  );
}

function Panel({ title, children, className = '' }) {
  return (
    <section className={`rounded-xl border border-rule bg-surface p-3.5 ${className}`}>
      <h2 className="mb-3 text-sm font-medium text-ink-2">{title}</h2>
      {children}
    </section>
  );
}

/**
 * Everything customers placed on the website in these days, and what became
 * of it: the owner asked how many came in and how many were taken or turned
 * down. Counted on its trading day like the rest of the page; a declined
 * order never becomes a bill, so this is the only place it is counted.
 */
function WebsiteRequests({ requests, onOpen }) {
  const share = (n) => (requests.total > 0 ? Math.round((n / requests.total) * 100) : 0);
  const tiles = [
    { key: 'all', label: 'Came in', orders: requests.total, tone: 'text-ink' },
    { key: 'accepted', label: 'Accepted', orders: requests.accepted.orders, value: requests.accepted.value, tone: 'text-good', pct: true },
    { key: 'declined', label: 'Declined', orders: requests.declined.orders, value: requests.declined.value, tone: requests.declined.orders ? 'text-bad' : 'text-ink', pct: true },
    { key: 'cancelled', label: 'Cancelled by customer', orders: requests.cancelled.orders, value: requests.cancelled.value, tone: 'text-ink', pct: true },
    ...(requests.waiting.orders ? [{ key: 'waiting', label: 'Still waiting', orders: requests.waiting.orders, value: requests.waiting.value, tone: 'text-warn' }] : []),
  ];
  return (
    <div className={`grid w-full grid-cols-2 gap-2 border-t border-rule pt-3 ${tiles.length === 5 ? 'sm:grid-cols-5' : 'sm:grid-cols-4'}`} aria-label="Website orders placed">
      {tiles.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => onOpen(t.key)}
          aria-label={`${t.label}: ${t.orders}. Show these orders`}
          className="rounded-lg bg-surface-2 px-3 py-2 text-left transition-colors hover:bg-surface-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-400"
        >
          <p className="text-xs text-ink-3">{t.label}</p>
          <p className={`text-lg font-semibold tabular-nums ${t.tone}`}>
            {t.orders}
            {t.pct && <span className="ml-1 text-xs font-normal text-ink-3">({share(t.orders)}%)</span>}
          </p>
          {t.value != null && <p className="text-xs tabular-nums text-ink-3">{formatPKR(t.value)}</p>}
        </button>
      ))}
    </div>
  );
}
